import { useUser } from "contexts/userContext/userContext";
import { GetUploadEndpointApiRequest } from "../api/MailsController";
import { useEffect, useState } from "react";

const FILES_PREFIX = "files.";
const JUMBOMAIL_DOMAIN = "jumbomail.me";

interface IBucketName {
  userId?: number;
  encryptedMailId?: string;
  isMvcLoginBucketName?: boolean;
}

/**
 * Fetches a custom bucket name from the server 
 * when there is no possibility to get it from the client side.
 * 
 * Read more:
 * https://docs.google.com/document/d/1ln66w1g4VoxM2lueWM_W5aidahiA8XYGrZ_ngqRHnWs/edit?usp=sharing
 */
const useBucketName = (props?: IBucketName) => {
  const { getUserData } = useUser();
  const [bucketName, setBucketName] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(
    props?.userId || props?.encryptedMailId || !props?.isMvcLoginBucketName
  );

  const mvcLoginBucketName = getUserData()?.BucketName;

  const getBucketNameByUserId = async (userId: number) => {
    const response = await GetUploadEndpointApiRequest({
      userId,
    });

    if (response.status === "success") {
      setBucketName(response.data);
    }
    setIsFetching(false);
  };

  const getBucketNameByEncryptedMailId = async (encryptedMailId: string) => {
    const response = await GetUploadEndpointApiRequest({
      encryptedMailId,
    });

    if (response.status === "success") {
      setBucketName(response.data);
    }
    setIsFetching(false);
  };

  const getBucketName = async () => {
    const response = await GetUploadEndpointApiRequest({});

    if (response.status === "success") {
      setBucketName(response.data);
    }
    setIsFetching(false);
  };

  useEffect(() => {
    // Check if the userId was passed as a prop but it is falsy value,
    // so don't go to other cases
    if (props?.userId !== undefined && props?.userId !== null) {
      if (props.userId) {
        getBucketNameByUserId(props.userId);
      }
    } else if (
      props?.encryptedMailId !== undefined &&
      props?.encryptedMailId !== null
    ) {
      if (props.encryptedMailId) {
        getBucketNameByEncryptedMailId(props.encryptedMailId);
      }
    } else {
      getBucketName();
    }
  }, [props?.userId, props?.encryptedMailId]);

  if (isFetching) return null;

  return _sanitizeBucketName(
    props?.isMvcLoginBucketName ? mvcLoginBucketName : bucketName
  );
};

const notCustomDomain = window.location.hostname.includes("jumbomail")
  ? window.location.hostname
  : JUMBOMAIL_DOMAIN;

const _sanitizeBucketName = (bucketName: string) => {
  if (!bucketName) {
    return notCustomDomain.replace(/^www\./, "");
  }
  if (bucketName && bucketName.includes("jumbomail1")) {
    return notCustomDomain.replace(/^www\./, "");
  } else {
    const bucketNameWithoutFilesPrefixAndWWW = bucketName
      .replace(FILES_PREFIX, "")
      .replace(/^www\./, "");

    return bucketNameWithoutFilesPrefixAndWWW;
  }
};

export default useBucketName;
